import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Trash2, Loader2, Plus, Calendar, Hash, ChevronDown, ChevronUp, Building2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface SavedQuote {
  id: string;
  name: string;
  sid: number;
  site_name: string | null;
  months: any[];
  scenarios: any[];
  bank_cost: any;
  results: any[];
  created_at: string;
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  }).format(n);
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function QuoteDetail({ quote }: { quote: SavedQuote }) {
  const { results, scenarios, bank_cost } = quote;
  const hasBankCost = bank_cost?.enabled;
  const monthCount = quote.months?.length || 0;

  if (!results || results.length === 0) {
    return <p className="text-sm text-slate-500 py-4">No results saved for this quote.</p>;
  }

  return (
    <div className="space-y-4">
      {/* Scenario summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {results.map((r: any, i: number) => {
          const sc = scenarios?.[i];
          const months = r.months || [];
          const totalRev = months.reduce((s: number, m: any) => s + (m.totalRev || 0), 0);
          const totalBankCost = months.reduce((s: number, m: any) => s + (m.totalBankCost || 0), 0);
          const totalProfit = months.reduce((s: number, m: any) => s + (m.profit || 0), 0);
          const colors = [
            'bg-indigo-50 border-indigo-100',
            'bg-emerald-50 border-emerald-100',
            'bg-amber-50 border-amber-100',
            'bg-rose-50 border-rose-100',
            'bg-cyan-50 border-cyan-100',
          ];

          return (
            <div key={r.scenarioId || i} className={`rounded-xl border p-4 ${colors[i % colors.length]}`}>
              <p className="text-sm font-semibold text-slate-900">{r.scenarioName || sc?.name || `Scenario ${i + 1}`}</p>
              {sc && (
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Pay: ${sc.paymentFixedFee}/txn + {sc.paymentRatePercent}% &middot;
                  Out: ${sc.payoutFixedFee}/txn + {sc.payoutRatePercent}%
                </p>
              )}
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Revenue</span>
                  <span className="font-semibold text-slate-900">{fmt(totalRev)}</span>
                </div>
                {hasBankCost && (
                  <div className="flex justify-between text-slate-500">
                    <span>Bank Costs</span>
                    <span>-{fmt(totalBankCost)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200/60 pt-1.5">
                  <span>{hasBankCost ? 'Profit' : 'Revenue'}</span>
                  <span>{fmt(hasBankCost ? totalProfit : totalRev)}</span>
                </div>
                {monthCount > 0 && (
                  <p className="text-[11px] text-slate-400">
                    Avg {fmt((hasBankCost ? totalProfit : totalRev) / monthCount)}/mo
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bank cost info */}
      {hasBankCost && (
        <div className="text-[12px] text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
          Bank cost: ${bank_cost.paymentFixedFee}/txn + {bank_cost.paymentRatePercent}% (pay) &middot;
          ${bank_cost.payoutFixedFee}/txn + {bank_cost.payoutRatePercent}% (out)
        </div>
      )}
    </div>
  );
}

export function SavedQuickQuotes() {
  const [quotes, setQuotes] = useState<SavedQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    loadQuotes();
  }, []);

  async function loadQuotes() {
    try {
      const { data, error } = await supabase
        .from('quick_quotes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuotes(data || []);
    } catch (err) {
      console.error('Failed to load quick quotes:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this quick quote?')) return;
    setDeleting(id);
    try {
      const { error } = await supabase.from('quick_quotes').delete().eq('id', id);
      if (error) throw error;
      setQuotes((prev) => prev.filter((q) => q.id !== id));
      if (expanded === id) setExpanded(null);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(null);
    }
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => (prev === id ? null : id));
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div>
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-amber-500" />
            <h1 className="page-title">Saved Quick Quotes</h1>
          </div>
          <p className="page-subtitle">View and manage your saved rate comparisons.</p>
        </div>
        <Link to="/quick-quote" className="btn-primary">
          <Plus className="w-4 h-4" />
          New Quick Quote
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : quotes.length === 0 ? (
        <div className="card text-center py-16">
          <div className="w-14 h-14 mx-auto mb-4 bg-amber-50 rounded-xl flex items-center justify-center">
            <Zap className="w-6 h-6 text-amber-500" />
          </div>
          <p className="text-[15px] font-medium text-slate-900">No saved quick quotes yet</p>
          <p className="text-[13px] text-slate-500 mt-1 mb-5">Create one to compare rate scenarios.</p>
          <Link to="/quick-quote" className="btn-primary">
            <Plus className="w-4 h-4" />
            New Quick Quote
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {quotes.map((q) => {
            const isExpanded = expanded === q.id;
            const scenarioCount = q.scenarios?.length || 0;
            const monthCount = q.months?.length || 0;

            return (
              <div key={q.id} className="card overflow-hidden">
                <button
                  onClick={() => toggleExpand(q.id)}
                  className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-slate-50/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-[15px] font-semibold text-slate-900 truncate">{q.name}</p>
                      <span className="badge-violet">{scenarioCount} scenario{scenarioCount !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-[12px] text-slate-500">
                      {q.site_name && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {q.site_name}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Hash className="w-3 h-3" />
                        {monthCount} month{monthCount !== 1 ? 's' : ''}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {fmtDate(q.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(q.id); }}
                      disabled={deleting === q.id}
                      className="btn-ghost text-slate-400 hover:text-red-500"
                    >
                      {deleting === q.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                    <QuoteDetail quote={q} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
